import React, { useCallback, useEffect, useState } from 'react';
import './MobileInstallTopBar.css';

const STORAGE_KEY = 'cdchk-pwa-install-topbar-dismissed';

const DESCRIPTION =
  'Get quick access to our app - install it on your device now.';

type BeforeInstallPromptEventExtended = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
};

function isRunningAsInstalledApp(): boolean {
  if (typeof window === 'undefined') return true;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** iPhone / iPod / iPad in browser. Includes iPadOS “desktop” Safari (MacIntel + touch). */
function isIosDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

/** Mobile-sized viewport (includes phone landscape) + touch phone/tablet, not narrow desktop. */
function isMobileInstallSurface(): boolean {
  if (typeof window === 'undefined') return false;
  const mobileViewport = window.matchMedia('(max-width: 1024px)').matches;
  if (!mobileViewport) return false;

  const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const mobileOrTabletUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
    navigator.userAgent
  );

  return coarsePointer || mobileOrTabletUA;
}

function useMobileInstallSurface(): boolean {
  const [showOnMobile, setShowOnMobile] = useState(() =>
    typeof window !== 'undefined' ? isMobileInstallSurface() : false
  );

  useEffect(() => {
    const update = () => setShowOnMobile(isMobileInstallSurface());

    const mqW = window.matchMedia('(max-width: 1024px)');
    const mqP = window.matchMedia('(pointer: coarse)');
    mqW.addEventListener('change', update);
    mqP.addEventListener('change', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    update();

    return () => {
      mqW.removeEventListener('change', update);
      mqP.removeEventListener('change', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return showOnMobile;
}

export default function MobileInstallTopBar() {
  const showOnMobileSurface = useMobileInstallSurface();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEventExtended | null>(null);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [installed, setInstalled] = useState(false);
  const [iosInstructionsOpen, setIosInstructionsOpen] = useState(false);

  const canShowIosBanner =
    isIosDevice() && !isRunningAsInstalledApp() && showOnMobileSurface;

  useEffect(() => {
    if (isRunningAsInstalledApp()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      if (readDismissed()) return;
      setDeferredPrompt(e as BeforeInstallPromptEventExtended);
    };

    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const handleDismiss = useCallback(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
    setDeferredPrompt(null);
    setIosInstructionsOpen(false);
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return;
    }
    if (canShowIosBanner) {
      setIosInstructionsOpen(true);
    }
  }, [deferredPrompt, canShowIosBanner]);

  if (isRunningAsInstalledApp()) {
    return null;
  }

  const showBar =
    showOnMobileSurface &&
    !installed &&
    !dismissed &&
    (deferredPrompt != null || canShowIosBanner);

  if (!showBar && !iosInstructionsOpen) {
    return null;
  }

  const logoSrc = `${process.env.PUBLIC_URL}/logo192.png`;

  return (
    <>
      {showBar ? (
        <div className="mobile-install-topbar" role="region" aria-label="Install app">
          <button
            type="button"
            className="mobile-install-topbar__close"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            ×
          </button>
          <img
            className="mobile-install-topbar__logo"
            src={logoSrc}
            alt=""
            width={36}
            height={36}
            decoding="async"
          />
          <p className="mobile-install-topbar__text">{DESCRIPTION}</p>
          <button type="button" className="mobile-install-topbar__install" onClick={handleInstall}>
            Install
          </button>
        </div>
      ) : null}

      {iosInstructionsOpen ? (
        <div
          className="mobile-install-ios-backdrop"
          role="presentation"
          onClick={() => setIosInstructionsOpen(false)}
        />
      ) : null}
      {iosInstructionsOpen ? (
        <div
          className="mobile-install-ios-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-install-ios-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id="mobile-install-ios-title" className="mobile-install-ios-sheet__title">
            Add to Home Screen
          </h2>
          {/* Legacy iOS Safari copy kept for reference:
              1. Tap the Share button (square with arrow) in Safari's toolbar.
              2. Scroll down and tap Add to Home Screen.
              3. Tap Add. Open the app from your home screen anytime.
          */}
          <ol className="mobile-install-ios-sheet__steps">
            <li>
              In Safari, tap <strong>...</strong> then <strong>Share</strong>. On some devices, you may see the Share
              button directly <span className="mobile-install-ios-sheet__hint">(square with arrow)</span>.
            </li>
            <li>
              Scroll down and tap <strong>Add to Home Screen</strong>. If you do not see it, tap{' '}
              <strong>Edit Actions</strong> and add it.
            </li>
            <li>
              Tap <strong>Add</strong>. If shown, keep <strong>Open as Web App</strong> on for app-like launch.
            </li>
          </ol>
          <button
            type="button"
            className="mobile-install-ios-sheet__ok"
            onClick={() => setIosInstructionsOpen(false)}
          >
            Got it
          </button>
        </div>
      ) : null}
    </>
  );
}
